import { useMemo } from "react";
import { parseNativeAppInfo } from "%/appVersion";

/**
 * 멍투데이 앱·웹·OS 환경을 통합 감지하는 훅.
 *
 * 5가지 환경:
 *  - app-ios     : 멍투데이 iOS 네이티브 앱(WebView, UA: MT_I)
 *  - app-android : 멍투데이 Android 네이티브 앱(WebView, UA: MT_A)
 *  - web-ios     : iPhone/iPad/iPod Safari·Chrome 등 일반 모바일 웹(iOS)
 *  - web-android : Android Chrome 등 일반 모바일 웹(Android)
 *  - web-desktop : Windows/Mac/Linux PC 브라우저
 *
 * 사용 예:
 *   const { isAppIOS, isAppAndroid, isMobileWeb, isDesktopWeb, env } = usePlatform();
 *   if (env === "app-android") ...    // 안드로이드 앱
 *   if (!isAppAndroid)         ...    // 안드로이드 앱이 아닌 모든 환경
 */

export type PlatformEnv =
  | "app-ios"
  | "app-android"
  | "web-ios"
  | "web-android"
  | "web-desktop";

export interface PlatformInfo {
  /** 통합 환경 식별자 */
  env: PlatformEnv;

  /** 멍투데이 앱(WebView) 안에서 실행 중인가? */
  isApp: boolean;
  /** 멍투데이 iOS 앱? (UA: MT_I) */
  isAppIOS: boolean;
  /** 멍투데이 Android 앱? (UA: MT_A) */
  isAppAndroid: boolean;

  /** 일반 모바일 브라우저? (iOS·Android 의 Safari/Chrome 등, 앱 제외) */
  isMobileWeb: boolean;
  /** 데스크탑 브라우저? */
  isDesktopWeb: boolean;

  /** OS 가 iOS 인가? (앱 + 모바일웹 모두 포함) */
  isIOS: boolean;
  /** OS 가 Android 인가? (앱 + 모바일웹 모두 포함) */
  isAndroid: boolean;

  /** 모바일 폼팩터 (앱 + 모바일웹) */
  isMobile: boolean;
  /** 데스크탑 폼팩터 */
  isDesktop: boolean;
}

export const usePlatform = (): PlatformInfo => {
  return useMemo(() => {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const nativeAppInfo = parseNativeAppInfo(ua);

    // ── 1. 멍투데이 앱(WebView) — 최우선 식별
    const isAppIOS = nativeAppInfo.platform === "ios";
    const isAppAndroid = nativeAppInfo.platform === "android";
    const isApp = isAppIOS || isAppAndroid;

    // ── 2. OS 감지 (UA 표준 패턴)
    //   iPadOS 13+ 는 Mac 처럼 보고하므로 maxTouchPoints 로 보정
    const isOSiOS =
      /iPad|iPhone|iPod/i.test(ua) ||
      (typeof navigator !== "undefined" &&
        navigator.platform === "MacIntel" &&
        navigator.maxTouchPoints > 1);
    const isOSAndroid = /Android/i.test(ua);

    // ── 3. 폼팩터
    const isMobileWeb = !isApp && (isOSiOS || isOSAndroid);
    const isDesktopWeb = !isApp && !isMobileWeb;

    // ── 4. 통합 환경 식별자
    const env: PlatformEnv = isAppIOS
      ? "app-ios"
      : isAppAndroid
        ? "app-android"
        : isOSiOS
          ? "web-ios"
          : isOSAndroid
            ? "web-android"
            : "web-desktop";

    return {
      env,
      isApp,
      isAppIOS,
      isAppAndroid,
      isMobileWeb,
      isDesktopWeb,
      isIOS: isAppIOS || isOSiOS,
      isAndroid: isAppAndroid || isOSAndroid,
      isMobile: isApp || isMobileWeb,
      isDesktop: isDesktopWeb,
    };
  }, []);
};
